/**
 * Expression rewriter presets for the expression-rewriter experiment.
 * Each preset defines a starting expression, optional target, and goal.
 */

export interface RewriterPreset {
  id: string;
  start: string;
  target?: string;
  goal: 'expand' | 'factor' | 'simplify';
  advancedOnly: boolean;
}

export const REWRITER_PRESETS: readonly RewriterPreset[] = [
  // Foundation presets
  {
    id: 'expand-simple',
    start: '2 * (x + 3)',
    target: '2x + 6',
    goal: 'expand',
    advancedOnly: false,
  },
  {
    id: 'expand-product',
    start: '(2x - 1)(x + 3)',
    target: '2x^2 + 5x - 3',
    goal: 'expand',
    advancedOnly: false,
  },
  {
    id: 'simplify-numeric',
    start: 'log2(8)',
    target: '3',
    goal: 'simplify',
    advancedOnly: false,
  },

  // Advanced presets
  {
    id: 'rationalize',
    start: '(3 + sqrt(2)) / (3 - sqrt(2))',
    target: '(11 + 6*sqrt(2)) / 7',
    goal: 'simplify',
    advancedOnly: true,
  },
  {
    id: 'exponent-laws',
    start: 'x^3 * x^2 / x^1',
    target: 'x^4',
    goal: 'simplify',
    advancedOnly: true,
  },
  {
    id: 'log-properties',
    start: 'log(xy) - log(x)',
    target: 'log(y)',
    goal: 'simplify',
    advancedOnly: true,
  },
];

/**
 * Validate that a preset has all required fields and valid structure.
 */
export function isValidPreset(p: RewriterPreset): boolean {
  // id must be non-empty string
  if (typeof p.id !== 'string' || p.id.length === 0) {
    return false;
  }

  // start must be a non-empty string
  if (typeof p.start !== 'string' || p.start.length === 0) {
    return false;
  }

  // target is optional but must be string if present
  if (p.target !== undefined && typeof p.target !== 'string') {
    return false;
  }

  // goal must be one of the allowed values
  if (!['expand', 'factor', 'simplify'].includes(p.goal)) {
    return false;
  }

  // advancedOnly must be boolean
  if (typeof p.advancedOnly !== 'boolean') {
    return false;
  }

  return true;
}

/**
 * Validate that all presets have unique IDs.
 */
export function validatePresetIds(presets: readonly RewriterPreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) {
      return false;
    }
    ids.add(preset.id);
  }
  return true;
}
